import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Code2 } from 'lucide-react';
import { Badge } from '../ui';
import { apiClient } from '../../api/client';
import { useSession } from '../../context/SessionContext';
import { parseSymbolSearchResults } from '../ide/graph/utils/transformers';
import { classifyError } from './errors';
import PanelState from './PanelState';

/**
 * Code search across the indexed codebase (spec §10.5 "Suche").
 *
 * Calls `apiClient.searchSymbols` directly rather than through `useCKB()`
 * because a search that finds nothing must not raise a global error toast —
 * it is an empty state, not a failure.
 *
 * @param {object} props
 * @param {string} props.query - the *submitted* query, not the live input.
 * @param {string|null} [props.selectedId]
 * @param {(symbol: {id:string,name:string,kind:string,file:string}) => void} props.onSelect
 */
export function SymbolResults({ query, selectedId = null, onSelect }) {
  const { t } = useTranslation();
  const { sessionId } = useSession();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    if (!query?.trim()) {
      setResults(null);
      setError(null);
      return undefined;
    }
    if (!sessionId) {
      setError(new Error('No active session'));
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .searchSymbols(sessionId, query.trim())
      .then((response) => {
        if (!cancelled) setResults(parseSymbolSearchResults(response.result));
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setResults([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query, sessionId]);

  useEffect(() => run(), [run]);

  if (loading) return <PanelState status="loading" compact />;
  if (error) {
    return <PanelState status={classifyError(error)} error={error} onRetry={run} compact />;
  }
  if (results === null) {
    return <p className="etch-knowledge__rail-empty">{t('ide.searchHint')}</p>;
  }
  if (results.length === 0) {
    return <p className="etch-knowledge__rail-empty">{t('knowledge.noSearchResults')}</p>;
  }

  return (
    <ul className="etch-knowledge__list" aria-label={t('knowledge.symbols')}>
      {results.map((symbol) => (
        <li key={symbol.id}>
          <button
            type="button"
            className="etch-knowledge__list-item"
            aria-current={symbol.id === selectedId ? 'true' : undefined}
            onClick={() => onSelect?.(symbol)}
          >
            <span className="etch-knowledge__list-icon" aria-hidden="true">
              <Code2 size={15} />
            </span>
            <span className="etch-knowledge__list-body">
              <span className="etch-knowledge__list-title">{symbol.name}</span>
              {symbol.file && <span className="etch-knowledge__list-meta">{symbol.file}</span>}
            </span>
            {symbol.kind && <Badge>{symbol.kind}</Badge>}
          </button>
        </li>
      ))}
    </ul>
  );
}

export default SymbolResults;
